//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

const clockPattern = /^(\d+):(\d{2})$/
const wordsPattern = /^(?:(\d+(?:[.,]\d+)?)\s*h)?\s*(?:(\d+(?:[.,]\d+)?)\s*m)?$/
const barePattern = /^(\d+(?:[.,]\d+)?)$/

function toDecimal (raw: string): number {
  return Number(raw.replace(',', '.'))
}

/**
 * Parses duration text into decimal hours. A bare number is hours.
 * Returns null when the text is empty or not an accepted form.
 *
 * @public
 */
export function parseDurationHours (input: string): number | null {
  const text = input.trim().toLowerCase()
  if (text.length === 0 || text.includes('-')) {
    return null
  }

  const clock = clockPattern.exec(text)
  if (clock !== null) {
    const minutes = Number(clock[2])
    if (minutes > 59) {
      return null
    }
    return Number(clock[1]) + minutes / 60
  }

  if (text.includes(':')) {
    return null
  }

  const words = wordsPattern.exec(text)
  if (words !== null && (words[1] !== undefined || words[2] !== undefined)) {
    const hours = words[1] !== undefined ? toDecimal(words[1]) : 0
    const minutes = words[2] !== undefined ? toDecimal(words[2]) : 0
    return hours + minutes / 60
  }

  const bare = barePattern.exec(text)
  if (bare !== null) {
    return toDecimal(bare[1])
  }

  return null
}

/**
 * Formats decimal hours as duration text, rounded to the nearest minute.
 * Display only: the stored number is not rewritten.
 *
 * @public
 */
export function formatDurationHours (hours: number): string {
  const totalMinutes = Math.round(hours * 60)
  const wholeHours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  if (wholeHours === 0) {
    return `${minutes}m`
  }
  if (minutes === 0) {
    return `${wholeHours}h`
  }
  return `${wholeHours}h ${minutes}m`
}

/**
 * Title shown on both workslot duration inputs.
 *
 * @public
 */
export const durationInputTitle = 'Duración (ej. 25m, 00:25, 1h 30m)'

/**
 * Resolves a duration field on blur. Zero and unparsed text keep the current value.
 *
 * @public
 */
export function resolveDurationInput (
  input: string,
  currentMs: number
): { emit: boolean, milliseconds: number, text: string } {
  const hours = parseDurationHours(input)
  if (hours === null || hours <= 0) {
    return {
      emit: false,
      milliseconds: currentMs,
      text: formatDurationHours(currentMs / 3_600_000)
    }
  }

  const milliseconds = hours * 60 * 60 * 1000
  return {
    emit: true,
    milliseconds,
    text: formatDurationHours(hours)
  }
}

/**
 * Minutes the issue time presenter shows for a stored hour number.
 * Matches TimePresenter: an 8-hour day, then hours, then rounded minutes.
 */
function displayedMinutes (hours: number): number {
  const days = Math.floor(hours / 8)
  const wholeHours = Math.floor(hours % 8)
  const minutes = Math.round((hours % 1) * 60)
  return days * 8 * 60 + wholeHours * 60 + minutes
}

/**
 * Hours to write from an estimate popup.
 * Invalid text and a value that displays the same minutes as the stored hours are skipped.
 *
 * @public
 */
export function applyEstimationText (text: string, storedHours: number): number | undefined {
  const hours = parseDurationHours(text)
  if (hours === null || displayedMinutes(hours) === displayedMinutes(storedHours)) {
    return undefined
  }
  return hours
}
